import React from "react";
import { focusOnFirstError } from "./focusUtils";
import Select from "react-select";

const education = [
  "Undergraduate (UG)",
  "Postgraduate (PG)",
  "Research / Doctoral Programs",
].map((edu) => ({
  value: edu,
  label: edu,
}));

const DegreesByCountry = {
  IN: {
    bachelor: [
      "PG Diploma Courses (in Digital Marketing, AI/ML, etc.)",
      "B.Tech / B.E. (Bachelor of Technology / Engineering)",
      "B.Sc. (Bachelor of Science – General / CS / IT / Math / Physics / etc.)",
      "BCA (Bachelor of Computer Applications)",
      "BBA (Bachelor of Business Administration)",
      "B.Com (Bachelor of Commerce)",
      "B.A. (Bachelor of Arts – General / English / Psychology / etc.)",
      "LLB (Bachelor of Law – 5 Year Integrated or 3 Year)",
      "MBBS (Bachelor of Medicine & Bachelor of Surgery)",
      "B.Pharm (Bachelor of Pharmacy)",
      "B.Des / BFA (Bachelor of Design / Fine Arts)",
      "B.Arch (Bachelor of Architecture)",
      "Diploma Courses (Polytechnic, ITI, etc.)",
    ],
    master: [
      "M.Tech / M.E. (Master of Technology / Engineering)",
      "M.Sc. (Master of Science – CS / Data Science / Physics / etc.)",
      "MCA (Master of Computer Applications)",
      "MBA / PGDM (Master of Business Administration / Post Grad Diploma in Management)",
      "M.Com (Master of Commerce)",
      "M.A. (Master of Arts – Economics / Sociology / etc.)",
      "LLM (Master of Law)",
      "M.Pharm (Master of Pharmacy)",
      "MD / MS (Medical PG)",
    ],
    research: [
      "Ph.D. in Computer Science",
      "Ph.D. in Engineering / Technology",
      "Ph.D. in Management / Business Administration",
      "Ph.D. in Physics",
      "Ph.D. in Chemistry",
      "Ph.D. in Mathematics",
      "Ph.D. in Environmental Science",
      "Ph.D. in Biotechnology / Life Sciences",
      "Ph.D. in Economics",
      "Ph.D. in Psychology",
      "Ph.D. in English / Literature",
      "Ph.D. in Education",
      "Ph.D. in Sociology / Social Work",
      "Ph.D. in Law",
      "Ph.D. in Pharmacy",
      "Ph.D. in Political Science",
      "Ph.D. in Public Policy / Development Studies",
    ],
  },

  US: {
    bachelor: [
      "B.S. in Computer Science – Tech, Software, AI, and Data",
      "B.S. in Mechanical Engineering – Core Engineering & Innovation",
      "B.S. in Business Administration – Management, Finance, Marketing",
      "B.A. in Psychology – Human Behavior, Mental Health, Research",
      "B.S. in Biology – Pre-Med, Life Sciences, Research",
      "B.S. in Nursing (BSN) – Healthcare & Clinical Practice",
      "B.A. in Political Science – Law, Policy, Governance",
      "B.A. in Communication – Media, PR, Journalism",
      "B.F.A. in Graphic Design – Creative Arts, Visual Design",
      "B.S. in Environmental Science – Sustainability, Ecology, Climate",
      "B.A. in English Literature – Writing, Education, Humanities",
      "B.S. in Data Science – Analytics, AI, Business Intelligence",
    ],
    master: [
      "M.S. in Computer Science",
      "MBA (Master of Business Administration)",
      "M.S. in Data Science / Analytics",
      "M.S. in Electrical or Mechanical Engineering",
      "M.S. in Public Health (MPH)",
      "M.S. in Nursing (MSN) / Nurse Practitioner (NP)",
      "M.Ed. (Master of Education)",
      "M.A. in Psychology / Counseling",
      "M.A. in International Relations / Political Science",
      "M.F.A. (Master of Fine Arts) in Design / Visual Arts",
    ],
  },
};

const yearOptions = Array.from({ length: 2028 - 2024 + 1 }, (_, i) => {
  const year = 2028 - i;
  return { value: year, label: year.toString() };
});

const employers = [
  "Fortune 500 / Large Multinational Corporation (MNC)",
  "Mid-Sized Enterprise (500–5000 Employees)",
  "Small & Medium Enterprise (SME) (<500 Employees)",
  "Startup / Early-Stage Company",
  "Government / Public Sector",
  "Non-Profit / NGO",
  "Education & Research (School, University, R&D)",
  "Freelancer / Self-Employed / Consultant",
  "Not Employed",
].map((employ) => ({
  label: employ,
  value: employ,
}));

const roles = [
  "Not Applicable",
  "Freelancer / Self-Employed / Entrepreneur",
  "Research & Education",
  "Creative & Design",
  "Sales & Marketing",
  "Business & Strategy",
  "Technical / Engineering",
  "Managerial",
  "Executive / C-Suite",
  "Public Service",
].map((role) => ({
  value: role,
  label: role,
}));

const experience = [
  "0 - 2 years",
  "3 - 5 years",
  "6 - 10 years",
  "10 + years",
  "Student / Fresher",
].map((exp) => ({
  label: exp,
  value: exp,
}));

const EducationForm = ({
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
  inputRefs,
  customSelectStyles,
  careerLaunch,
}) => {
  const validateField = (name, value) => {
    const errors = {};
    if (careerLaunch) {
      switch (name) {
        case "education":
          if (!value) errors.education = "Education level is required";
          break;
        case "specialization":
          if (!value) errors.specialization = "Specialization is required";
          break;
        default:
          break;
      }
      return errors;
    } else {
      switch (name) {
        case "education":
          if (!value) errors.education = "Education level is required";
          break;
        case "specialization":
          if (!value) errors.specialization = "Specialization is required";
          break;
        case "current_employer":
          if (!value) errors.current_employer = "Current employer is required";
          break;
        case "currentRole":
          if (!value) errors.currentRole = "Current role is required";
          break;
        case "gender":
          if (!value) errors.gender = "Experience Level is required";
          break;
        default:
          break;
      }
      return errors;
    }
  };

  const getDegreesByEducationLevel = (country, educationLevel) => {
    if (!country || !educationLevel) return [];

    const key = educationLevel.toLowerCase().includes("undergraduate")
      ? "bachelor"
      : educationLevel.toLowerCase().includes("postgraduate")
      ? "master"
      : "research";

    const degrees = DegreesByCountry[country]?.[key] || [];
    return degrees.map((deg) => ({ value: deg, label: deg }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    const value = selectedOption ? selectedOption.value : "";

    setFormValues((prev) => ({ ...prev, [name]: value }));
    const fieldErrors = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      [name]: fieldErrors[name] || "", // Clear error if valid
      ...(fieldErrors[name] ? fieldErrors : {}),
    }));

    if (Object.keys(fieldErrors).length > 0) {
      focusOnFirstError(fieldErrors, inputRefs);
    }
  };

  return (
    <div className="form-section card" style={{ height: "365px" }}>
      <h5 className="mt-2 mb-0">Your Highest Qualification</h5>
      <div className="row m-3 mb-0">
        <div className="col-lg-4">
          <div className="form-group">
            <label
              className="small-label fw-bold"
              style={{ textTransform: "capitalize" }}
            >
              Graduation level*
            </label>
            <Select
              ref={inputRefs.education}
              name="education"
              options={education}
              onChange={(option) =>
                handleSelectChange(option, { name: "education" })
              }
              value={
                education.find(
                  (option) => option.value === formValues.education
                ) || null
              }
              placeholder="Select Your Education"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
              isSearchable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {formErrors.education && (
              <p className="text-red">{formErrors.education}</p>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="form-group">
            <label
              className="small-label fw-bold"
              style={{ textTransform: "capitalize" }}
            >
              Expected graduation year
            </label>
            <Select
              ref={inputRefs.yearOfCompletion}
              name="yearOfCompletion"
              options={yearOptions}
              onChange={(option) =>
                handleSelectChange(option, { name: "yearOfCompletion" })
              }
              value={
                yearOptions.find(
                  (option) => option.value === formValues.yearOfCompletion
                ) || null
              }
              placeholder="Select Your Education"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
              isSearchable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {/* <select
              ref={inputRefs.yearOfCompletion}
              name="yearOfCompletion"
              value={formValues.yearOfCompletion || ""}
              onChange={handleChange}
              className="form-control"
            >
              <option value="" hidden>
                Select Year of Completion
              </option>
              {Array.from({ length: 2028 - 2024 + 1 }, (_, i) => 2028 - i).map(
                (year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                )
              )}
            </select> */}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">Specialization *</label>
            {/* <select
              ref={inputRefs.specialization}
              name="specialization"
              value={formValues.specialization || ""}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="" hidden>
                Select Specialization
              </option>
              {degrees.map((degree, index) => (
                <option key={index} value={degree.value}>
                  {degree.label}
                </option>
              ))}
            </select> */}
            <Select
              ref={inputRefs.specialization}
              name="specialization"
              options={getDegreesByEducationLevel(
                formValues.country,
                formValues.education
              )}
              onChange={(option) =>
                handleSelectChange(option, { name: "specialization" })
              }
              value={
                getDegreesByEducationLevel(
                  formValues.country,
                  formValues.education
                ).find(
                  (option) => option.value === formValues.specialization
                ) || null
              }
              placeholder="Select Specialization"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
              isSearchable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {formErrors.specialization && (
              <p className="text-red">{formErrors.specialization}</p>
            )}
          </div>
        </div>
      </div>
      <h5 className="mb-0 mt-2">Current Employment</h5>
      <div className="row m-2 mb-0">
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">Current Employer *</label>
            <Select
              ref={inputRefs.current_employer}
              name="current_employer"
              options={employers}
              onChange={(option) =>
                handleSelectChange(option, { name: "current_employer" })
              }
              value={
                careerLaunch
                  ? employers.find(
                      (option) => option.value === "Not Employed"
                    ) || {
                      value: "Not Employed",
                      label: "Not Employed",
                    }
                  : employers.find(
                      (option) => option.value === formValues.current_employer
                    ) || null
              }
              placeholder="Select Employer"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
              isSearchable
              isDisabled={careerLaunch} // <- Disable if careerLaunch is true
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {formErrors.current_employer && !careerLaunch && (
              <p className="text-red">{formErrors.current_employer}</p>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">Current Role *</label>
            <Select
              ref={inputRefs.currentRole}
              name="currentRole"
              options={roles}
              onChange={(option) =>
                handleSelectChange(option, { name: "currentRole" })
              }
              value={
                careerLaunch
                  ? roles.find(
                      (option) => option.value === "Not Applicable"
                    ) || {
                      value: "Not Applicable",
                      label: "Not Applicable",
                    }
                  : roles.find(
                      (option) => option.value === formValues.currentRole
                    ) || null
              }
              placeholder="Select Role"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
              isSearchable
              isDisabled={careerLaunch}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {formErrors.currentRole && !careerLaunch && (
              <p className="text-red">{formErrors.currentRole}</p>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">
              Current Experience Level *
            </label>
            <Select
              ref={inputRefs.gender}
              name="gender"
              options={experience}
              onChange={(option) =>
                handleSelectChange(option, { name: "gender" })
              }
              value={
                careerLaunch
                  ? experience.find(
                      (option) => option.value === "Student / Fresher"
                    ) || {
                      value: "Student / Fresher",
                      label: "Student / Fresher",
                    }
                  : experience.find(
                      (option) => option.value === formValues.gender
                    ) || null
              }
              placeholder="Select Experience"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
              isSearchable
              isDisabled={careerLaunch}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {formErrors.gender && !careerLaunch && (
              <p className="text-red">{formErrors.gender}</p>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        .country-select-disabled {
          pointer-events: none;
          opacity: 0;
        }

        /* Override react-select to match form-control style */
        :global(.form-control-select) {
          padding: 0 !important;
          border: none !important;
        }

        :global(.form-control-select > div) {
          border-color: #ced4da !important;
        }

        :global(.form-control-select > div:focus-within) {
          border-color: #80bdff !important;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
        }

        /* Fix for the select components alignment */
        :global(.select__placeholder),
        :global(.select__single-value) {
          display: flex !important;
          align-items: center !important;
          height: 36px !important;
        }

        .form-section {
          overflow-y: auto;
          overflow-x: hidden;
        }

        @media (max-width: 992px) {
          .form-section {
            height: auto;
          }

          .form-group {
            margin-bottom: 1rem;
          }

          textarea {
            min-width: 100% !important;
          }
        }

        @media (max-width: 768px) {
          .form-group label {
            font-size: 0.875rem;
          }

          input.form-control,
          .form-control-select,
          textarea {
            font-size: 0.875rem;
          }

          .form-check-label {
            font-size: 0.85rem;
          }

          textarea {
            min-height: 70px !important;
          }

          .form-section {
            padding: 10px;
          }
        }

        @media (max-width: 576px) {
          .col-lg-4,
          .col-lg-12 {
            width: 100%;
            flex: 0 0 100%;
            max-width: 100%;
          }

          .form-control-select > div {
            height: auto !important;
          }

          textarea {
            width: 100% !important;
            min-width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default EducationForm;
